import React from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '@/hooks/useReducedMotion';

interface Testimonial {
  id: string;
  quote: string;
  body: string;
  name: string;
  role: string;
}

export default function AboutTestimonialsMission() {
  const prefersReducedMotion = useReducedMotion();

  // Testimonials data
  const testimonials: Testimonial[] = [
    {
      id: '1',
      quote: "You get the person doing the work, start to finish.",
      body: "No account manager and no hand-off to a junior. The person on your first call is the person who names it, builds it and wires the system behind it.",
      name: 'Direct access',
      role: 'What you get',
    },
    {
      id: '2',
      quote: 'The offer gets settled before a single pixel does.',
      body: "Most sites underperform because the offer underneath them was never made clear. I fix that in order: positioning and pricing first, design second.",
      name: 'Strategy first',
      role: 'Where the work starts',
    },
    {
      id: '3',
      quote: 'Your domain gets treated like equity, not decoration.',
      body: "A premium name is an asset on your balance sheet. The job is to make it earn, and to leave you holding something that could one day be sold.",
      name: 'Asset thinking',
      role: 'What we optimise for',
    },
    {
      id: '4',
      quote: 'The system keeps working after launch week.',
      body: "Automations that answer enquiries and follow up leads keep running long after the site goes live, so the business keeps moving while you deliver the work.",
      name: 'After launch',
      role: 'What you keep',
    },
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const headingItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  return (
    <motion.section
      id="testimonials-mission"
      aria-label="Client testimonials and mission"
      variants={sectionVariants}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className="relative w-full py-20 md:py-24 bg-gradient-to-b from-white via-sky-50 to-slate-50 overflow-hidden"
    >
      {/* Decorative background blobs */}
      <div className="pointer-events-none absolute top-1/4 -left-32 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-60" />
      <div className="pointer-events-none absolute bottom-1/4 -right-32 w-96 h-96 bg-[#0000FF]/10 rounded-full blur-3xl opacity-60" />
      <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0000FF]/5 rounded-full blur-3xl opacity-40" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Heading block */}
          <motion.div
            variants={headingVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mb-16 md:mb-20"
          >
            <motion.div variants={headingItemVariants}>
              <p className="text-xs md:text-sm font-semibold tracking-wide text-slate-500 uppercase mb-4">
                HOW I WORK
              </p>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 mb-6">
                What building with Attain Domains is actually like.
              </h2>
            </motion.div>

            <motion.div variants={headingItemVariants}>
              <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
                Nobody hires me for just a website. They hire me to turn a premium domain into a business they can live on, grow and eventually sell, with someone who has done exactly that twice.
              </p>
            </motion.div>
          </motion.div>

          {/* Testimonials grid */}
          <motion.ul
            variants={gridVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-16 md:mb-20"
          >
            {testimonials.map((testimonial, index) => (
              <motion.li
                key={testimonial.id}
                variants={cardVariants}
                animate={
                  prefersReducedMotion
                    ? {}
                    : {
                        y: [0, -4, 0],
                      }
                }
                transition={
                  prefersReducedMotion
                    ? {}
                    : {
                        duration: 6 + index * 0.5,
                        repeat: Infinity,
                        repeatType: 'mirror',
                        ease: 'easeInOut',
                        delay: index * 0.2,
                      }
                }
                className="rounded-2xl border border-slate-200/70 bg-white/80 shadow-sm p-6 flex flex-col h-full hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <blockquote className="flex-1 space-y-4">
                  <p className="text-base md:text-lg font-semibold text-slate-900 leading-snug">
                    "{testimonial.quote}"
                  </p>
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                    {testimonial.body}
                  </p>
                </blockquote>
                <footer className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-sm font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-xs text-slate-500">{testimonial.role}</p>
                </footer>
              </motion.li>
            ))}
          </motion.ul>

          {/* Mission block */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.3 }}
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center mt-16"
          >
            <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-4">
              My mission
            </h3>
            <div className="space-y-4 text-slate-700 leading-relaxed">
              <p className="text-base md:text-lg">
                I've been building brands, websites and systems for over 20 years, and I've founded, built and sold two online marketplaces. I don't run Attain Domains because I need more projects. I run it because I like turning a smart domain into a business that feels like its owner, pays them well, and can one day be sold.
              </p>
              <p className="text-base md:text-lg">
                If we work together, my goal is simple: to treat your domain like an asset, your
                time like it actually matters, and your launch like the moment everything you've
                built so far finally clicks into place.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
}

